import { useAutoNavigate } from '@betterment/js-runtime';
import { Box, Card, CenteredLayout, H2, Text } from '@betterment/samba-react';

import { DownloadCsvTemplate } from './DownloadCsvTemplate';
import { UploadCsvForm } from './UploadCsvForm';

import { FullPageLoader } from '../../../../Shared/Components/FullPageLoader';
import urlBuilder from '../../../../Shared/url-builder';
import { UploadCsvFormValues, isInvalidFeeAttemptBatch } from '../data';
import { BulkFeeAttemptQuery } from '../graphql/query.bulkFeeAttempt.generated';
import { UploadCsvValidationErrorBanner } from '../upload-csv-validation-error-banner/UploadCsvValidationErrorBanner';
import { useUploadCsvSubmit } from '../useUploadCsvSubmit';

export const OnDemandBillingStart = () => {
  const navigate = useAutoNavigate();

  const handleSuccess = (bulkFeeAttempt: BulkFeeAttemptQuery['firm']['bulkFeeAttempt']) => {
    if (!bulkFeeAttempt) {
      throw new Error('Failed to fetch bulk fee attempt');
    }

    if (isInvalidFeeAttemptBatch(bulkFeeAttempt.feeAttempts)) {
      void navigate(urlBuilder.getOnDemandBillingResolvePage.absolute({ id: bulkFeeAttempt.id }));
    } else {
      void navigate(urlBuilder.getOnDemandBillingReviewPage.absolute({ id: bulkFeeAttempt.id }));
    }
  };

  const { isSubmittingUploadedCsv, uploadCsvValidationError, handleSubmit } = useUploadCsvSubmit({
    variant: 'create',
    onSuccess: handleSuccess,
  });

  const handleFormSubmit = async (values: UploadCsvFormValues) => {
    await handleSubmit({ csvFile: values.csvFile, name: values.name });
  };

  if (isSubmittingUploadedCsv) {
    return (
      <FullPageLoader
        text={
          <>
            <Text variant="text200" css={{ fontWeight: 'medium' }}>
              Preparing for review
            </Text>
            <Text variant="text100">This may take a few minutes...</Text>
          </>
        }
      />
    );
  }

  return (
    <Card css={{ p: 'space.300', boxShadow: 'none' }}>
      <Box css={{ mb: 'space.200', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <CenteredLayout size="medium">
          <Box css={{ width: '584px' }}>
            {uploadCsvValidationError && (
              <Box css={{ mb: 'space.400' }}>
                <UploadCsvValidationErrorBanner errorType={uploadCsvValidationError} />
              </Box>
            )}
            <H2 variant="title400" css={{ pb: 'space.300' }}>
              How to bill on-demand:
            </H2>
            <DownloadCsvTemplate />
            <UploadCsvForm onSubmit={handleFormSubmit} />
          </Box>
        </CenteredLayout>
      </Box>
    </Card>
  );
};
